import React, { useState, useEffect, useCallback, useRef } from "react";
import { X, Copy, Check, Pencil, Eye, ExternalLink, FileText, Loader2, Download, FileDown, ChevronDown } from "lucide-react";
import MarkdownMessage from "./MarkdownMessage";

export default function MarkdownViewerModal({ open, file, onClose }) {
    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [editing, setEditing] = useState(false);
    const [copied, setCopied] = useState(false);
    const [saveMenuOpen, setSaveMenuOpen] = useState(false);
    const [savingPdf, setSavingPdf] = useState(false);
    const contentRef = useRef(null);
    const saveMenuRef = useRef(null);

    useEffect(() => {
        if (!open || !file?.url) return;
        setLoading(true);
        setError(false);
        setEditing(false);
        setContent("");
        // Candidatos: para arquivo em domínio externo (R2/CDN sem CORS), tenta
        // o proxy primeiro; para mesmo domínio, tenta direto primeiro.
        const q = `/api/file-proxy?url=${encodeURIComponent(file.url)}`;
        const base = process.env.REACT_APP_BACKEND_URL;
        let sameOrigin = false;
        try { sameOrigin = new URL(file.url, window.location.href).origin === window.location.origin; } catch { /* noop */ }
        const proxies = [q];
        if (base) proxies.unshift(`${base}${q}`);
        const candidates = sameOrigin ? [file.url, ...proxies] : [...proxies, file.url];
        (async () => {
            for (const u of candidates) {
                try {
                    const r = await fetch(u);
                    if (!r.ok) continue;
                    const text = await r.text();
                    // Rejeita respostas HTML (ex.: fallback do SPA/nginx devolvendo
                    // o index.html quando a rota /api não chega no backend Go).
                    const ctype = (r.headers.get("content-type") || "").toLowerCase();
                    const head = text.slice(0, 300).trimStart().toLowerCase();
                    if (ctype.includes("text/html") || head.startsWith("<!doctype") || head.startsWith("<html")) continue;
                    setContent(text);
                    setLoading(false);
                    return;
                } catch { /* tenta o próximo */ }
            }
            setError(true);
            setLoading(false);
        })();
    }, [open, file?.url]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [open, onClose]);

    const copyAll = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(content);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch { /* noop */ }
    }, [content]);

    useEffect(() => {
        if (!saveMenuOpen) return;
        const onClick = (e) => {
            if (saveMenuRef.current && !saveMenuRef.current.contains(e.target)) setSaveMenuOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [saveMenuOpen]);

    const baseName = (file?.filename || "arquivo").replace(/\.[^.]+$/, "");

    const saveTxt = useCallback(() => {
        const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${baseName}.txt`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        setSaveMenuOpen(false);
    }, [content, baseName]);

    const savePdf = useCallback(async () => {
        setSaveMenuOpen(false);
        setSavingPdf(true);
        try {
            const html2pdf = (await import("html2pdf.js")).default;
            const src = contentRef.current;
            const clone = document.createElement("div");
            clone.innerHTML = src ? src.innerHTML : `<pre style="white-space:pre-wrap">${content.replace(/</g, "&lt;")}</pre>`;
            clone.style.cssText = "background:#ffffff;color:#111111;padding:32px;font-family:Georgia,serif;font-size:13px;line-height:1.7;width:700px;";
            clone.querySelectorAll("*").forEach((el) => {
                el.style.color = "#111111";
                el.style.backgroundColor = "transparent";
                el.style.borderColor = "#cccccc";
            });
            await html2pdf().set({
                margin: 10,
                filename: `${baseName}.pdf`,
                image: { type: "jpeg", quality: 0.95 },
                html2canvas: { scale: 2, backgroundColor: "#ffffff" },
                jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
                pagebreak: { mode: ["avoid-all", "css", "legacy"] },
            }).from(clone).save();
        } catch { /* noop */ }
        setSavingPdf(false);
    }, [content, baseName]);

    if (!open || !file) return null;

    const isMd = (file.filename || "").toLowerCase().endsWith(".md");

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-0 sm:p-6"
            data-testid="md-viewer-modal"
        >
            <div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm fade-in"
                onClick={onClose}
                data-testid="md-viewer-backdrop"
            />
            <div className="relative w-full h-full sm:h-[88vh] sm:max-w-3xl bg-card border-0 sm:border border-border sm:rounded-lg shadow-2xl flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center gap-2 px-3 sm:px-4 py-2.5 border-b border-border bg-muted/40 shrink-0">
                    <FileText className="w-4 h-4 text-primary shrink-0" />
                    <span className="text-sm font-medium truncate flex-1 min-w-0" data-testid="md-viewer-filename">
                        {file.filename}
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                        <button
                            type="button"
                            onClick={copyAll}
                            disabled={loading || error}
                            className="flex items-center gap-1.5 text-[11px] font-mono text-muted-foreground hover:text-primary transition-colors px-2 py-1.5 rounded-sm hover:bg-muted/60 disabled:opacity-40"
                            data-testid="md-viewer-copy-btn"
                            aria-label="Copiar conteúdo"
                        >
                            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                            <span className="hidden sm:inline">{copied ? "Copiado" : "Copiar"}</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setEditing((v) => !v)}
                            disabled={loading || error}
                            className={`flex items-center gap-1.5 text-[11px] font-mono transition-colors px-2 py-1.5 rounded-sm hover:bg-muted/60 disabled:opacity-40 ${editing ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-primary"}`}
                            data-testid="md-viewer-edit-btn"
                            aria-label={editing ? "Visualizar" : "Editar"}
                        >
                            {editing ? <Eye className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
                            <span className="hidden sm:inline">{editing ? "Visualizar" : "Editar"}</span>
                        </button>
                        <div className="relative" ref={saveMenuRef}>
                            <button
                                type="button"
                                onClick={() => setSaveMenuOpen((v) => !v)}
                                disabled={loading || error || savingPdf}
                                className={`flex items-center gap-1.5 text-[11px] font-mono transition-colors px-2 py-1.5 rounded-sm hover:bg-muted/60 disabled:opacity-40 ${saveMenuOpen ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-primary"}`}
                                data-testid="md-viewer-save-btn"
                                aria-label="Salvar arquivo"
                            >
                                {savingPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                                <span className="hidden sm:inline">Salvar</span>
                                <ChevronDown className="w-3 h-3 hidden sm:block" />
                            </button>
                            {saveMenuOpen && (
                                <div className="absolute right-0 top-full mt-1.5 w-44 bg-card border border-border rounded-md shadow-xl overflow-hidden z-10" data-testid="md-viewer-save-menu">
                                    <button
                                        type="button"
                                        onClick={savePdf}
                                        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs text-foreground hover:bg-muted/60 transition-colors text-left"
                                        data-testid="md-viewer-save-pdf"
                                    >
                                        <FileDown className="w-4 h-4 text-red-400" />
                                        Salvar como PDF
                                    </button>
                                    <button
                                        type="button"
                                        onClick={saveTxt}
                                        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs text-foreground hover:bg-muted/60 transition-colors text-left border-t border-border"
                                        data-testid="md-viewer-save-txt"
                                    >
                                        <FileText className="w-4 h-4 text-slate-400" />
                                        Salvar como .txt
                                    </button>
                                </div>
                            )}
                        </div>
                        <a
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 text-[11px] font-mono text-muted-foreground hover:text-primary transition-colors px-2 py-1.5 rounded-sm hover:bg-muted/60"
                            data-testid="md-viewer-newtab-btn"
                            aria-label="Abrir em nova aba"
                        >
                            <ExternalLink className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Nova aba</span>
                        </a>
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-1.5 rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors ml-1"
                            data-testid="md-viewer-close-btn"
                            aria-label="Fechar"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto min-h-0">
                    {loading && (
                        <div className="h-full flex items-center justify-center">
                            <Loader2 className="w-7 h-7 animate-spin text-primary" />
                        </div>
                    )}
                    {!loading && error && (
                        <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
                            <FileText className="w-8 h-8 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">
                                Não foi possível carregar o conteúdo aqui.
                            </p>
                            <a
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs font-medium text-primary hover:underline flex items-center gap-1.5"
                            >
                                <ExternalLink className="w-3.5 h-3.5" /> Abrir em nova aba
                            </a>
                        </div>
                    )}
                    {!loading && !error && editing && (
                        <textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            className="w-full h-full min-h-[50vh] p-4 bg-transparent text-sm font-mono leading-relaxed resize-none outline-none text-foreground"
                            spellCheck={false}
                            data-testid="md-viewer-textarea"
                        />
                    )}
                    {!loading && !error && !editing && (
                        <div ref={contentRef} className="p-4 sm:p-6" data-testid="md-viewer-content">
                            {isMd ? (
                                <MarkdownMessage content={content} />
                            ) : (
                                <pre className="text-sm font-mono whitespace-pre-wrap break-words leading-relaxed text-foreground">{content}</pre>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
